import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/components/feature/AuthContext";
import { useToast } from "@/components/base/Toast";
import CrmLayout from "@/components/feature/CrmLayout";
import StatsBar from "./components/StatsBar";
import FilterBar from "./components/FilterBar";
import LeadsTable from "./components/LeadsTable";
import LeadsCardView from "./components/LeadsCardView";
import LeadsListView from "./components/LeadsListView";
import Pagination from "./components/Pagination";
import BulkActions from "./components/BulkActions";
import AddLeadModal from "./components/AddLeadModal";
import { exportToCSV, formatExportDate } from "@/utils/exportUtils";
import { fetchApi } from "@/services/apiService";
import type { Lead, LeadSource, LeadStage, Priority, Industry, ScoreCategory } from "@/mocks/leadsData";

const ITEMS_PER_PAGE = 10;

interface LeadStats {
  total: number;
  newToday: number;
  contacted: number;
  converted: number;
  unassigned: number;
  stale: number;
}

export default function LeadsPage() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ── Data state ─────────────────────────────────────────────
  const [leads, setLeads] = useState<Lead[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<LeadStats>({ total: 0, newToday: 0, contacted: 0, converted: 0, unassigned: 0, stale: 0 });

  // ── Filter state ───────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState<LeadSource | "all">("all");
  const [stageFilter, setStageFilter] = useState<LeadStage | "all">("all");
  const [assignedFilter, setAssignedFilter] = useState<string | "all">("all");
  const [industryFilter, setIndustryFilter] = useState<Industry | "all">("all");
  const [priorityFilter, setPriorityFilter] = useState<Priority | "all">("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [scoreFilter, setScoreFilter] = useState<ScoreCategory>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [staleFilterActive, setStaleFilterActive] = useState(false);

  // ── UI state ───────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Lead | null>(null);
  const [viewMode, setViewMode] = useState<"table" | "card" | "list">("table");
  const [staleBannerDismissed, setStaleBannerDismissed] = useState(false);
  const [unassignedBannerDismissed, setUnassignedBannerDismissed] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);

  // ── Close export dropdown on outside click ──────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) setExportOpen(false);
    };
    if (exportOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [exportOpen]);

  // ── Restore banner state ────────────────────────────────────
  useEffect(() => {
    if (sessionStorage.getItem("staleBannerDismissed") === "1") setStaleBannerDismissed(true);
    if (sessionStorage.getItem("unassignedBannerDismissed") === "1") setUnassignedBannerDismissed(true);
  }, []);

  // ── URL filter param ────────────────────────────────────────
  useEffect(() => {
    const agentParam = searchParams.get("agent") || searchParams.get("assigned");
    if (agentParam) {
      setAssignedFilter(agentParam);
    }
    if (searchParams.get("filter") === "stale") {
      setStaleFilterActive(true);
      setStaleBannerDismissed(false);
      sessionStorage.removeItem("staleBannerDismissed");
      setTimeout(() => window.scrollTo({ top: 400, behavior: "smooth" }), 200);
    }
  }, [searchParams]);

  // ── Fetch leads ─────────────────────────────────────────────
  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({
        page: String(currentPage),
        limit: String(ITEMS_PER_PAGE),
        search,
        source: sourceFilter,
        stage: stageFilter,
        assigned: assignedFilter,
        industry: industryFilter,
        priority: priorityFilter,
        date: dateFilter,
        score: scoreFilter,
        stale: String(staleFilterActive),
      });
      if (sortBy) {
        query.append("sortBy", sortBy);
        query.append("sortDir", sortDir);
      }

      const res = await fetchApi<{ items?: Lead[]; total?: number; totalPages?: number }>(`/leads?${query.toString()}`, undefined, {
        items: [],
        total: 0,
        totalPages: 1,
      });

      const items = res?.items || [];
      setLeads(items);
      setTotal(res?.total || items.length);
      setTotalPages(res?.totalPages || 1);
    } catch (err: any) {
      showToast(err.message || "Failed to load leads", "error");
      setLeads([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, search, sourceFilter, stageFilter, assignedFilter, industryFilter, priorityFilter, dateFilter, scoreFilter, staleFilterActive, sortBy, sortDir]);

  // ── Fetch stats ─────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    try {
      const defaultStats: LeadStats = {
        total: 0,
        newToday: 0,
        contacted: 0,
        converted: 0,
        unassigned: 0,
        stale: 0,
      };
      const res = await fetchApi<LeadStats>('/leads/stats', undefined, defaultStats);
      if (res) setStats(res);
    } catch {}
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  // ── Handlers ───────────────────────────────────────────────
  const clearFilters = useCallback(() => {
    setSearch(""); setSourceFilter("all"); setStageFilter("all");
    setAssignedFilter("all"); setIndustryFilter("all"); setPriorityFilter("all");
    setDateFilter("all"); setScoreFilter("all"); setSortBy(null);
    setStaleFilterActive(false); setCurrentPage(1);
  }, []);

  const handleSort = (column: string) => {
    if (sortBy === column) setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    else { setSortBy(column); setSortDir("desc"); }
    setCurrentPage(1);
  };

  const toggleSelect = (id: number) => setSelectedIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => (selectedIds.size === leads.length ? setSelectedIds(new Set()) : setSelectedIds(new Set(leads.map((l) => l.id))));
  const clearSelection = () => setSelectedIds(new Set());

  const handleAdd = () => { setEditingLead(null); setModalOpen(true); };
  const handleEdit = (lead: Lead) => { setEditingLead(lead); setModalOpen(true); };
  const handleView = (lead: Lead) => navigate(`/leads/${lead.id}`);
  const handleDelete = (lead: Lead) => setDeleteConfirm(lead);

  // ── Create / Update ─────────────────────────────────────────
  const handleSave = async (data: Omit<Lead, "id" | "lastActivity">, id?: number) => {
    setIsSaving(true);
    try {
      if (id) {
        await fetchApi(`/leads/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(data),
        });
        showToast("Lead updated successfully", "success");
      } else {
        await fetchApi('/leads', {
          method: 'POST',
          body: JSON.stringify(data),
        });
        showToast("Lead created successfully", "success");
      }
      setModalOpen(false);
      await fetchLeads();
      await fetchStats();
    } catch (err: any) {
      showToast(err.message || "Failed to save lead", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────────
  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await fetchApi(`/leads/${deleteConfirm.id}`, { method: 'DELETE' });
      showToast("Lead deleted", "success");
      setDeleteConfirm(null);
      setSelectedIds((prev) => { const n = new Set(prev); n.delete(deleteConfirm.id); return n; });
      await fetchLeads();
      await fetchStats();
    } catch (err: any) {
      showToast(err.message || "Failed to delete lead", "error");
    }
  };

  // ── Bulk actions ────────────────────────────────────────────
  const handleBulkAssign = async (agent: string) => {
    try {
      await fetchApi('/leads/bulk/assign', {
        method: 'PATCH',
        body: JSON.stringify({ ids: [...selectedIds], assigned: agent }),
      });
      showToast(`Assigned to ${agent}`, "success");
      clearSelection();
      await fetchLeads();
    } catch (err: any) {
      showToast(err.message || "Bulk assign failed", "error");
    }
  };

  const handleBulkStage = async (stage: LeadStage) => {
    try {
      await fetchApi('/leads/bulk/stage', {
        method: 'PATCH',
        body: JSON.stringify({ ids: [...selectedIds], stage }),
      });
      showToast(`Moved to ${stage}`, "success");
      clearSelection();
      await fetchLeads();
    } catch (err: any) {
      showToast(err.message || "Bulk stage change failed", "error");
    }
  };

  const handleBulkDelete = async () => {
    try {
      await fetchApi('/leads/bulk', {
        method: 'DELETE',
        body: JSON.stringify({ ids: [...selectedIds] }),
      });
      showToast(`${selectedIds.size} lead(s) deleted`, "success");
      clearSelection();
      await fetchLeads();
      await fetchStats();
    } catch (err: any) {
      showToast(err.message || "Bulk delete failed", "error");
    }
  };

  // ── Auto assign ─────────────────────────────────────────────
  const handleAutoAssign = async () => {
    showToast(`Auto-assigning ${stats.unassigned} leads...`, "info");
    try {
      const agents = ["Rajesh Kumar", "Priya Sharma", "Amit Patel", "Sneha Rao"];
      await fetchApi('/leads/auto-assign', {
        method: 'PATCH',
        body: JSON.stringify({ agents }),
      });
      showToast(`${stats.unassigned} leads assigned successfully`, "success");
      await fetchLeads();
      await fetchStats();
    } catch (err: any) {
      showToast("Auto-assign failed", "error");
    }
  };

  // ── Banners ─────────────────────────────────────────────────
  const handleDismissStaleBanner = () => { setStaleBannerDismissed(true); sessionStorage.setItem("staleBannerDismissed", "1"); };
  const handleDismissUnassignedBanner = () => { setUnassignedBannerDismissed(true); sessionStorage.setItem("unassignedBannerDismissed", "1"); };

  // ── Export ──────────────────────────────────────────────────
  const buildExportRows = (data: Lead[]) => data.map((l) => ({
    "Lead Name": l.name, Company: l.company, Phone: l.phone, Email: l.email,
    Source: l.source, Stage: l.stage, Priority: l.priority, Score: l.score,
    "Assigned To": l.assigned, "Deal Value": l.value, "Last Activity": l.lastActivity,
  }));

  const handleExportCSV = () => {
    showToast("Preparing export...", "info");
    setTimeout(() => { exportToCSV(buildExportRows(leads), `leads-export-${formatExportDate()}.csv`); showToast("Download started", "success"); }, 800);
    setExportOpen(false);
  };

  const handleExportPDF = () => { setExportOpen(false); setTimeout(() => window.print(), 600); };

  const handleExportSelectedCSV = () => {
    const selected = leads.filter((l) => selectedIds.has(l.id));
    showToast("Preparing export...", "info");
    setTimeout(() => { exportToCSV(buildExportRows(selected), `leads-selected-${formatExportDate()}.csv`); showToast("Download started", "success"); }, 800);
    setExportOpen(false);
  };

  return (
    <CrmLayout title="Leads">
      <div className="p-5 md:p-6 space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Leads</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {loading ? "Loading..." : `${total} total leads`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* View Switcher */}
            <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
              {(["table", "card", "list"] as const).map((mode, i) => (
                <button key={mode} onClick={() => setViewMode(mode)}
                  className={`p-2 transition-colors ${i > 0 ? "border-l border-gray-200" : ""} ${viewMode === mode ? "bg-indigo-600 text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}
                  title={`${mode} view`}>
                  <i className={`text-sm ${mode === "table" ? "ri-table-line" : mode === "card" ? "ri-layout-grid-line" : "ri-list-check"}`} />
                </button>
              ))}
            </div>

            <button onClick={() => navigate("/leads/import")}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm transition-colors">
              <i className="ri-upload-2-line" />
            </button>

            <div className="relative" ref={exportRef}>
              <button onClick={() => setExportOpen(!exportOpen)}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm transition-colors whitespace-nowrap">
                <i className="ri-download-line" /> Export
              </button>
              {exportOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-200 shadow-lg w-52 py-2 z-50">
                  <button onClick={handleExportCSV} className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                    <i className="ri-file-excel-2-line text-green-600" /> Export as CSV
                  </button>
                  <button onClick={handleExportPDF} className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                    <i className="ri-file-pdf-2-line text-red-500" /> Export as PDF
                  </button>
                  <div className="border-t border-gray-100 my-1" />
                  <button onClick={handleExportSelectedCSV} disabled={selectedIds.size === 0}
                    className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm cursor-pointer ${selectedIds.size > 0 ? "text-gray-700 hover:bg-gray-50" : "text-gray-300 cursor-not-allowed"}`}>
                    <i className={`ri-checkbox-multiple-line ${selectedIds.size > 0 ? "text-indigo-500" : "text-gray-300"}`} />
                    Export Selected ({selectedIds.size})
                  </button>
                </div>
              )}
            </div>

            <button onClick={handleAdd}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-all whitespace-nowrap">
              <i className="ri-add-line" /> Add Lead
            </button>
          </div>
        </div>

        {/* Stats bar — real data */}
        <StatsBar
          total={stats.total}
          newToday={stats.newToday}
          contacted={stats.contacted}
          converted={stats.converted}
        />

        {/* Stale banner */}
        {!staleBannerDismissed && stats.stale > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                <i className={`${staleFilterActive ? "ri-filter-3-line" : "ri-error-warning-line"} text-red-600`} />
              </div>
              <p className="text-sm text-red-700 font-medium">
                {staleFilterActive
                  ? `Showing stale leads only — ${stats.stale} leads with no activity in 7+ days`
                  : `${stats.stale} leads have had no activity in 7 or more days`}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={() => { setStaleFilterActive((p) => !p); setCurrentPage(1); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${staleFilterActive ? "bg-white border border-red-300 text-red-600 hover:bg-red-50" : "bg-red-600 hover:bg-red-700 text-white"}`}>
                {staleFilterActive ? "Clear Filter" : "Filter Stale Leads"}
              </button>
              <button onClick={handleDismissStaleBanner} className="text-gray-400 hover:text-gray-600">
                <i className="ri-close-line" />
              </button>
            </div>
          </div>
        )}

        {/* Unassigned banner */}
        {!unassignedBannerDismissed && stats.unassigned > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-alert-line text-amber-600" />
              </div>
              <p className="text-sm text-amber-700 font-medium">
                {stats.unassigned} leads are unassigned and waiting for assignment
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={handleAutoAssign}
                className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium transition-colors whitespace-nowrap">
                Auto-Assign Now
              </button>
              <button onClick={() => navigate("/settings/assignment")}
                className="text-xs text-amber-600 hover:text-amber-700 font-medium whitespace-nowrap">
                Go to Assignment Rules
              </button>
              <button onClick={handleDismissUnassignedBanner} className="text-gray-400 hover:text-gray-600">
                <i className="ri-close-line" />
              </button>
            </div>
          </div>
        )}

        <FilterBar
          search={search} onSearchChange={(v) => { setSearch(v); setCurrentPage(1); }}
          sourceFilter={sourceFilter} onSourceFilterChange={(v) => { setSourceFilter(v); setCurrentPage(1); }}
          stageFilter={stageFilter} onStageFilterChange={(v) => { setStageFilter(v); setCurrentPage(1); }}
          assignedFilter={assignedFilter} onAssignedFilterChange={(v) => { setAssignedFilter(v); setCurrentPage(1); }}
          industryFilter={industryFilter} onIndustryFilterChange={(v) => { setIndustryFilter(v); setCurrentPage(1); }}
          priorityFilter={priorityFilter} onPriorityFilterChange={(v) => { setPriorityFilter(v); setCurrentPage(1); }}
          dateFilter={dateFilter} onDateFilterChange={setDateFilter}
          scoreFilter={scoreFilter} onScoreFilterChange={(v) => { setScoreFilter(v); setCurrentPage(1); }}
          onClearFilters={clearFilters}
          staleFilterActive={staleFilterActive}
          onToggleStaleFilter={() => { setStaleFilterActive((p) => !p); setCurrentPage(1); }}
        />

        {selectedIds.size > 0 && (
          <BulkActions count={selectedIds.size} onAssign={handleBulkAssign} onChangeStage={handleBulkStage} onDelete={handleBulkDelete} onClear={clearSelection} />
        )}

        {/* Loading */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
          </div>
        ) : leads.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <i className="ri-user-search-line text-4xl text-gray-300" />
            </div>
            <p className="text-lg font-semibold text-gray-700 mb-1">No leads found</p>
            <p className="text-sm text-gray-500 mb-5">Try adjusting your filters or add a new lead</p>
            <div className="flex items-center justify-center gap-3">
              <button onClick={clearFilters} className="px-4 py-2.5 rounded-lg border border-indigo-300 text-indigo-600 hover:bg-indigo-50 text-sm font-medium whitespace-nowrap">Clear Filters</button>
              <button onClick={handleAdd} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold whitespace-nowrap">
                <i className="ri-add-line" /> Add Lead
              </button>
            </div>
          </div>
        ) : (
          <>
            {viewMode === "table" && (
              <LeadsTable leads={leads} selectedIds={selectedIds} onToggleSelect={toggleSelect} onToggleSelectAll={toggleSelectAll}
                allSelected={leads.length > 0 && selectedIds.size === leads.length} onView={handleView} onEdit={handleEdit} onDelete={handleDelete}
                sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
            )}
            {viewMode === "card" && <LeadsCardView leads={leads} onView={handleView} onEdit={handleEdit} onDelete={handleDelete} />}
            {viewMode === "list" && (
              <LeadsListView leads={leads} selectedIds={selectedIds} onToggleSelect={toggleSelect} onToggleSelectAll={toggleSelectAll}
                allSelected={leads.length > 0 && selectedIds.size === leads.length} onView={handleView} onEdit={handleEdit} onDelete={handleDelete} />
            )}
          </>
        )}

        {!loading && total > 0 && (
          <Pagination currentPage={currentPage} totalPages={totalPages} start={(currentPage - 1) * ITEMS_PER_PAGE + 1}
            end={Math.min(currentPage * ITEMS_PER_PAGE, total)} total={total} onPageChange={setCurrentPage} />
        )}

        {modalOpen && <AddLeadModal lead={editingLead} onClose={() => setModalOpen(false)} onSave={handleSave} isSaving={isSaving} />}

        {deleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-red-600 text-lg" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Delete Lead</h3>
                  <p className="text-sm text-gray-500">{deleteConfirm.name} — This cannot be undone.</p>
                </div>
              </div>
              <div className="flex items-center gap-2.5 justify-end">
                <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 whitespace-nowrap">Cancel</button>
                <button onClick={confirmDelete} className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-sm font-medium text-white whitespace-nowrap">Delete</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </CrmLayout>
  );
}